<template>
  <v-card class="ModalSiteHook" max-width="600px" dark>
    <v-card-title class="headline justify-center">
      Create Outgoing Hook
    </v-card-title>

    <v-card-text class="mt-6 text">
      <div>Method:</div>
      <v-select
        v-model="method"
        class="pa-0"
        :items="methods"
        item-text="label"
        item-value="value"
        return-object
        dense
        filled
        single-line
      ></v-select>

      <div>Event:</div>
      <v-select
        v-model="selectedEvent"
        class="pa-0"
        :items="events"
        item-text="label"
        item-value="value"
        return-object
        dense
        filled
        single-line
        @change="onEventChange"
      ></v-select>

      <!-- Form Selection (only for form_submission events) -->
      <div v-if="selectedEvent.value === 'form_submission'">
        <div class="mt-4">Form (optional):</div>
        <v-select
          v-model="selectedForm"
          class="pa-0"
          :items="availableForms"
          item-text="label"
          item-value="value"
          return-object
          dense
          filled
          single-line
          placeholder="Select a form"
          clearable
          @change="onFormChange"
        >
          <template v-slot:item="{ item }">
            <div>
              <div>{{ item.label }}</div>
              <div class="caption text-muted">{{ item.site }}</div>
            </div>
          </template>
        </v-select>

        <div class="mt-4">Form Scope (optional):</div>
        <v-text-field
          v-model="formScope"
          class="pa-0 mt-1"
          placeholder="Enter form scope/name"
          hide-details
          filled
          dense
        />
      </div>

      <div>URL</div>
      <v-text-field
        v-model="url"
        class="pa-0 mt-1"
        placeholder="Enter Webhook Url"
        hide-details
        filled
        dense
      />

      <div class="mt-5">Parameters</div>
      <v-textarea
        filled
        v-model="parameters"
        placeholder="
          key = value format e.g.
          text = site deployed successfully"
      ></v-textarea>

      <!-- Common Parameter Templates -->
      <div class="mt-4">
        <h6 class="mb-3">Common Templates</h6>
        <div class="parameter-templates">
          <v-card
            v-for="template in availableParameterTemplates"
            :key="template.name"
            class="parameter-template-card"
            @click="applyTemplate(template)"
            outlined
          >
            <v-card-text class="pa-3">
              <div class="template-icon">
                <v-icon small>{{ template.icon }}</v-icon>
              </div>
              <div class="template-name">{{ template.name }}</div>
            </v-card-text>
          </v-card>
        </div>
      </div>

      <!-- Variable Display (only for form_submission events with selected form) -->
      <div v-if="selectedEvent.value === 'form_submission' && selectedForm" class="mt-4">
        <div class="d-flex justify-space-between align-center mb-2">
          <h6>Available Variables</h6>
          <v-btn small text color="y1" @click="copyAllVariables">
            Copy All
          </v-btn>
        </div>
        
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Form Metadata
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="variable-list">
                <div v-for="variable in formMetadataVariables" :key="variable.name" class="variable-item">
                  <code>{{ variable.name }}</code>
                  <span class="variable-description">- {{ variable.description }}</span>
                  <v-btn x-small text color="y1" @click="copyVariable(variable.name)">
                    Copy
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel v-if="formFields.length > 0">
            <v-expansion-panel-header>
              Form Fields
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="variable-list">
                <div v-for="field in formFields" :key="field" class="variable-item">
                  <code>{{ getVariableName(field) }}</code>
                  <span class="variable-description">- {{ field }} field value</span>
                  <v-btn x-small text color="y1" @click="copyVariable(getVariableName(field))">
                    Copy
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>

    <v-card-actions class="mx-auto pa-6 justify-center">
      <v-btn @click="onCreate" color="y1">{{ actionType }}</v-btn>
      <v-btn @click="close" color="n5" class="ml-7">Cancel</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { Form as FormResource } from '@/resources/forge'

export default {
  name: 'ModalSiteHook',
  props: {
    webhook: Object,
  },
  data() {
    let webhookValue =
      this.webhook == undefined ? 'POST' : this.webhook.http_method
    let eventLabel =
      this.webhook == undefined ? 'Deployment Success' : this.webhook.event
    let eventValue =
      this.webhook == undefined
        ? 'deploy_success'
        : this.webhook.event.replaceAll(' ', '_').toLowerCase()
    if (eventValue.startsWith('deployment'))
      eventValue = eventValue.replace('deployment', 'deploy')
    return {
      events: [
        {
          label: 'Deployment Success',
          value: 'deploy_success',
        },
        {
          label: 'Deployment Failure',
          value: 'deploy_failure',
        },
        {
          label: 'Form submission',
          value: 'form_submission',
        },
        {
          label: 'Plugin analysis complete',
          value: 'plugin_analysis_complete',
        },
      ],
      methods: [
        {
          label: 'POST',
          value: 'POST',
        },
        {
          label: 'GET',
          value: 'GET',
        },
      ],
      method: { label: webhookValue, value: webhookValue },
      selectedEvent: { label: eventLabel, value: eventValue },
      url: this.webhook == undefined ? '' : this.webhook.url,
      parameters: this.webhook == undefined ? '' : this.webhook.parameters,
      actionType: this.webhook == undefined ? 'Add New Hook' : 'Update Hook',
      
      // New form-related fields
      selectedForm: null,
      formScope: this.webhook?.form_scope || '',
      availableForms: [],
      formFields: [],
      loadingForms: false,
    }
  },
  computed: {
    ...mapState({
      activeSite: (state) => state.sites.active,
    }),
    formMetadataVariables() {
      return [
        { name: '{{form_id}}', description: 'Unique form identifier' },
        { name: '{{form_name}}', description: "Form's internal name" },
        { name: '{{form_human_name}}', description: 'Human-readable form name' },
        { name: '{{form_scope}}', description: 'Form scope/name' },
        { name: '{{form_datum_id}}', description: 'Unique submission identifier' },
        { name: '{{form_submitted_at}}', description: 'ISO8601 timestamp' },
        { name: '{{form_fields}}', description: 'JSON array of form fields' },
        { name: '{{form_field_count}}', description: 'Number of fields' },
      ]
    },
    availableParameterTemplates() {
      if (this.selectedEvent.value === 'form_submission') {
        return [
          {
            name: 'Slack',
            icon: 'mdi-slack',
            content: `payload={"text": "New form submission received for {{form_human_name}} on {{form_submitted_at}}", "channel": "#general", "username": "Forge Bot", "icon_emoji": ":rocket:"}`
          },
          {
            name: 'Discord',
            icon: 'mdi-discord',
            content: `content = New {{form_human_name}} form submission received!
username = Forge Bot
avatar_url = https://getforge.com/logo.png`
          },
          {
            name: 'Email',
            icon: 'mdi-email',
            content: `to = admin@example.com
subject = New {{form_human_name}} form submission
body = A new form submission has been received for {{form_human_name}}.
form_data = {{form_fields}}`
          },
          {
            name: 'Zapier',
            icon: 'mdi-zap',
            content: `action = form_submission
form_name = {{form_human_name}}
form_data = {{form_fields}}
timestamp = {{form_submitted_at}}`
          },
          {
            name: 'Webhook',
            icon: 'mdi-webhook',
            content: `event_type = form_submission
form_id = {{form_id}}
form_name = {{form_human_name}}
data = {{form_fields}}`
          }
        ]
      } else if (this.selectedEvent.value === 'deploy_success') {
        return [
          {
            name: 'Slack',
            icon: 'mdi-slack',
            content: `payload={"text": "Site {{site_url}} deployed successfully! :white_check_mark:", "channel": "#deployments", "username": "Forge Bot", "icon_emoji": ":rocket:"}`
          },
          {
            name: 'Discord',
            icon: 'mdi-discord',
            content: `content = :white_check_mark: Site {{site_url}} deployed successfully!
username = Forge Bot
avatar_url = https://getforge.com/logo.png`
          },
          {
            name: 'Email',
            icon: 'mdi-email',
            content: `to = team@example.com
subject = Site {{site_url}} deployed successfully
body = The site {{site_url}} has been deployed successfully.`
          }
        ]
      } else if (this.selectedEvent.value === 'deploy_failure') {
        return [
          {
            name: 'Slack',
            icon: 'mdi-slack',
            content: `payload={"text": "Site {{site_url}} deployment failed! :x:", "channel": "#deployments", "username": "Forge Bot", "icon_emoji": ":warning:"}`
          },
          {
            name: 'Discord',
            icon: 'mdi-discord',
            content: `content = :x: Site {{site_url}} deployment failed!
username = Forge Bot
avatar_url = https://getforge.com/logo.png`
          },
          {
            name: 'Email',
            icon: 'mdi-email',
            content: `to = team@example.com
subject = Site {{site_url}} deployment failed
body = The site {{site_url}} deployment has failed. Please check the logs.`
          }
        ]
      } else {
        return [
          {
            name: 'Slack',
            icon: 'mdi-slack',
            content: `payload={"text": "{{event}} event occurred on {{timestamp}}", "channel": "#general", "username": "Forge Bot", "icon_emoji": ":bell:"}`
          },
          {
            name: 'Webhook',
            icon: 'mdi-webhook',
            content: `event_type = {{event}}
timestamp = {{timestamp}}
site_id = {{site_id}}`
          }
        ]
      }
    },
  },
  async created() {
    if (this.selectedEvent.value === 'form_submission') {
      await this.loadForms()
    }
  },
  methods: {
    async onEventChange() {
      if (this.selectedEvent.value === 'form_submission') {
        await this.loadForms()
      } else {
        this.selectedForm = null
        this.formFields = []
      }
    },
    async onFormChange() {
      if (this.selectedForm) {
        await this.loadFormFields()
      } else {
        this.formFields = []
      }
    },
    async loadForms() {
      if (this.loadingForms) return
      this.loadingForms = true
      
      try {
        const response = await FormResource.get({ site_id: this.activeSite.id })
        if (response.body && response.body.forms) {
          this.availableForms = response.body.forms.map(form => ({
            label: form.human_name || form.name,
            value: form.id,
            site: this.activeSite.url,
            name: form.name
          }))
        } else {
          this.availableForms = []
        }
        
        // Set selected form if editing existing webhook
        if (this.webhook?.form_id) {
          this.selectedForm = this.availableForms.find(f => f.value === this.webhook.form_id)
        }
      } catch (error) {
        console.error('Error loading forms:', error)
        this.availableForms = []
      } finally {
        this.loadingForms = false
      }
    },
    async loadFormFields() {
      if (!this.selectedForm) return
      
      try {
        const response = await FormResource.show({ id: this.selectedForm.value })
        this.formFields = response.body.form.fields || []
      } catch (error) {
        console.error('Error loading form fields:', error)
        this.formFields = []
      }
    },
    copyVariable(variable) {
      navigator.clipboard.writeText(variable).then(() => {
        // Variable copied to clipboard
      })
    },
    copyAllVariables() {
      const allVariables = [
        ...this.formMetadataVariables.map(v => v.name),
        ...this.formFields.map(field => this.getVariableName(field))
      ]
      const variablesText = allVariables.join('\n')
      navigator.clipboard.writeText(variablesText).then(() => {
        // All variables copied to clipboard
      })
    },
    getVariableName(field) {
      // Convert field name to snake_case
      const snakeCase = field
        .replace(/([A-Z])/g, '_$1') // Add underscore before capital letters
        .toLowerCase() // Convert to lowercase
        .replace(/^_/, '') // Remove leading underscore if present
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/[^a-z0-9_]/g, '') // Remove any non-alphanumeric characters except underscores
      
      return `{{${snakeCase}}}`
    },
    applyTemplate(template) {
      this.parameters = template.content
    },
    onCreate() {
      let params = {
        event: this.selectedEvent.value,
        http_method: this.method.value,
        parameters: this.parameters,
        url: this.url,
        site_id: this.activeSite.id,
      }
      
      // Add form-specific parameters
      if (this.selectedEvent.value === 'form_submission') {
        if (this.selectedForm) {
          params.form_id = this.selectedForm.value
        }
        if (this.formScope) {
          params.form_scope = this.formScope
        }
      }
      
      if (this.webhook == undefined)
        this.$store.dispatch('sites/addHook', params)
      else {
        params = { id: this.webhook.id, webhook_trigger: params }
        this.$store.dispatch('sites/updateHook', params, this.webhook.id)
      }
      this.$emit('close')
    },
    close() {
      this.$emit('close')
    }
  },
}
</script>

<style lang="scss" scoped>
$style: ModalSiteHook;
.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 600px;
    padding: 24px 24px 0;
  }
}
.text {
  font-size: 13px;
}
.error-main {
  background: #e94a53;
  border-radius: 2px;
}
.variable-list {
  .variable-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    &:last-child {
      border-bottom: none;
    }
    
    code {
      background: rgba($B1, 0.2);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: monospace;
      color: $B2;
      border: 1px solid rgba($B1, 0.3);
    }
    
    .variable-description {
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      margin-left: 8px;
    }
  }
}

.parameter-templates {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0 16px 0;
  
  .parameter-template-card {
    min-width: 120px;
    max-width: 120px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: rgba($B1, 0.05) !important;
    border: 1px solid rgba($B1, 0.2) !important;
    
    &:hover {
      background: rgba($B1, 0.1) !important;
      border-color: rgba($B1, 0.4) !important;
      transform: translateY(-2px);
    }
    
    .v-card__text {
      padding: 12px !important;
      text-align: center;
    }
    
    .template-icon {
      margin-bottom: 8px;
      
      .v-icon {
        color: $B2;
        font-size: 20px;
      }
    }
    
    .template-name {
      font-size: 12px;
      font-weight: 500;
      color: white;
    }
  }
}

// Override Vuetify expansion panel styles
::v-deep .v-expansion-panels {
  .v-expansion-panel {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    
    &::before {
      box-shadow: none !important;
    }
    
    .v-expansion-panel-header {
      background: rgba($B1, 0.1) !important;
      border: 1px solid rgba($B1, 0.3) !important;
      border-radius: 8px !important;
      margin-bottom: 8px;
      box-shadow: none !important;
      
      &:hover {
        background: rgba($B1, 0.15) !important;
      }
      
      .v-expansion-panel-header__icon {
        color: $B2 !important;
      }
    }
    
    .v-expansion-panel-content {
      background: rgba($B1, 0.05) !important;
      border: 1px solid rgba($B1, 0.2) !important;
      border-top: none !important;
      border-radius: 0 0 8px 8px !important;
      box-shadow: none !important;
    }
  }
}
</style>
